import React from "react";
import { AssessmentQuestion } from "./questionTypes";
import { AnswerData, QuestionAnswer } from "./answerTypes";
import { QuestionRenderer } from "./QuestionRenderer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const ADVISOR_LABELS: Record<string, string> = {
  accountant_cpa: 'Accountant / CPA',
  tax_planning_specialist: 'Tax Planning Specialist',
  bookkeeper: 'Bookkeeper',
  cross_border_tax: 'Cross-Border Tax Advisor',
  cfo_fractional: 'CFO / Fractional CFO',
  other_financial: 'Other Financial / Tax Advisor',
  corporate_business_lawyer: 'Corporate / Business Lawyer',
  estate_planning_lawyer: 'Estate Planning Lawyer',
  tax_lawyer: 'Tax Lawyer',
  securities_regulatory_lawyer: 'Securities / Regulatory Lawyer',
  employment_lawyer: 'Employment Lawyer',
  ip_lawyer: 'Intellectual Property Lawyer',
  partnership_shareholder_specialist: 'Partnership / Shareholder Specialist',
  other_legal: 'Other Legal Advisor',
  business_valuator_cbv: 'Chartered Business Valuator (CBV)',
  ma_advisor: 'M&A Advisor',
  succession_consultant: 'Succession Planning Consultant',
  dealer_liaison: 'Dealer / Broker Succession Liaison',
  transition_coach: 'Business Transition Coach',
  other_succession: 'Other Succession Specialist',
  insurance_advisor: 'Insurance Advisor',
  wealth_manager: 'Wealth Manager / Financial Planner',
  estate_trustee: 'Estate Trustee / Trust Officer',
  risk_management_consultant: 'Risk Management Consultant',
  other_wealth: 'Other Wealth / Risk Advisor',
};

const CONTACT_SUFFIXES = ['_name', '_email', '_firm', '_phone'];

interface AdvisorTypeSectionCardProps {
  advisorType: string;
  questions: AssessmentQuestion[];
  answers: AnswerData;
  onAnswerChange: (questionId: string, value: QuestionAnswer) => void;
  onNext: () => void;
  onFinish: () => void;
}

export function AdvisorTypeSectionCard({
  advisorType,
  questions,
  answers,
  onAnswerChange,
  onNext,
  onFinish,
}: AdvisorTypeSectionCardProps) {
  const label = ADVISOR_LABELS[advisorType] || advisorType.replace(/_/g, ' ');

  const contactQuestions = questions.filter((q) =>
    CONTACT_SUFFIXES.some((suffix) => q.id.endsWith(suffix))
  );
  const qualityQuestions = questions.filter(
    (q) => !CONTACT_SUFFIXES.some((suffix) => q.id.endsWith(suffix))
  );

  return (
    <Card className="border border-border bg-card/50">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold">{label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Contact fields: 2-column grid */}
        {contactQuestions.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {contactQuestions.map((q) => (
              <div key={q.id}>
                <QuestionRenderer
                  question={q}
                  answers={answers}
                  onAnswerChange={onAnswerChange}
                  onNext={onNext}
                  onFinish={onFinish}
                />
              </div>
            ))}
          </div>
        )}

        {/* Quality questions: full-width */}
        {qualityQuestions.map((q) => (
          <div key={q.id} className="border-t border-border pt-4">
            <QuestionRenderer
              question={q}
              answers={answers}
              onAnswerChange={onAnswerChange}
              onNext={onNext}
              onFinish={onFinish}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * Extracts the advisor type prefix from a question_key.
 * Returns null if the key does not match any known advisor type.
 */
export function getAdvisorTypeFromKey(questionKey: string): string | null {
  // Sort by length descending so longer prefixes match first
  const sortedKeys = Object.keys(ADVISOR_LABELS).sort(
    (a, b) => b.length - a.length
  );
  for (const prefix of sortedKeys) {
    if (questionKey.startsWith(prefix + '_')) {
      return prefix;
    }
  }
  return null;
}

export { ADVISOR_LABELS };
